import Cookies from "js-cookie";
import {
  NotificationCtaAction,
  parseNotificationCtaPayload,
} from "@/utils/notificationCta";
import {
  getNotificationCountryOrigin,
  NOTIFICATION_STATUS_CLICKED,
} from "@/utils/notificationHelpers";
import { stripInPrefix } from "@/utils/region";
import { getGuestId, getUserId } from "@/utils/session";

function parseCookieNumber(name) {
  const raw = Cookies.get(name);
  if (!raw) return 0;

  try {
    const parsed = JSON.parse(raw);
    const num = Number(parsed);
    return Number.isFinite(num) ? num : 0;
  } catch {
    const num = Number(String(raw).replace(/"/g, ""));
    return Number.isFinite(num) ? num : 0;
  }
}

function pickSearchFields(query = {}) {
  const searchId = Number(
    query.CityId ??
      query.cityId ??
      query.CountryId ??
      query.countryId ??
      query.RegionId ??
      query.regionId ??
      query.StateId ??
      query.stateId ??
      query.ActivityTypeId ??
      query.activityTypeId ??
      query.searchId ??
      0
  );

  const searchText = String(
    query.Keywords ??
      query.keywords ??
      query.searchText ??
      query.CityName ??
      query.cityName ??
      query.CountryName ??
      query.countryName ??
      query.q ??
      ""
  ).trim();

  return {
    searchId: Number.isFinite(searchId) ? searchId : 0,
    searchText,
  };
}

function buildEventDataJson(router, extra = {}) {
  try {
    return JSON.stringify({
      asPath: router.asPath,
      pathname: router.pathname,
      query: router.query,
      ...extra,
    });
  } catch {
    return "";
  }
}

/**
 * Map current Next.js route to notification tracking fields.
 */
export function resolveUserTrackingFromRoute(router) {
  const pathname = stripInPrefix(String(router.pathname || "/"));
  const query = router.query || {};
  const slug = String(query.slug || "").trim();

  let ctaAction = 0;
  let module = "";
  let bookingStatus = "";
  let notificationStatus = String(
    query.notificationStatus ?? query.NotificationStatus ?? ""
  ).trim();

  if (pathname === "/" || pathname === "/index") {
    ctaAction = NotificationCtaAction.Homepage;
    module = "Home";
  } else if (pathname === "/holiday") {
    ctaAction = NotificationCtaAction.HolidayLanding;
    module = "Holiday";
  } else if (pathname === "/holiday/list") {
    ctaAction = NotificationCtaAction.HolidayListing;
    module = "Holiday";
  } else if (pathname === "/holiday/[slug]") {
    ctaAction = NotificationCtaAction.HolidayDetails;
    module = "Holiday";
  } else if (pathname === "/activity" || pathname === "/activity/index") {
    ctaAction = NotificationCtaAction.ActivityLanding;
    module = "Activity";
  } else if (pathname === "/activity/list") {
    ctaAction = NotificationCtaAction.ActivityListing;
    module = "Activity";
  } else if (pathname === "/activity/[slug]") {
    ctaAction = NotificationCtaAction.ActivityDetails;
    module = "Activity";
  } else if (pathname === "/cart") {
    ctaAction = NotificationCtaAction.Cart;
    module = "Cart";
  } else if (pathname === "/place/[slug]") {
    ctaAction = NotificationCtaAction.CountryCityPage;
    module = "Location";
  } else if (pathname === "/deals") {
    ctaAction = NotificationCtaAction.Deals;
    module = "Deals";
  } else if (pathname === "/deals/[slug]") {
    ctaAction = NotificationCtaAction.SeasonalDeals;
    module = "Deals";
  } else if (pathname === "/coupons") {
    ctaAction = NotificationCtaAction.CouponListing;
    module = "Coupon";
  } else if (pathname === "/coupons/[slug]") {
    ctaAction = NotificationCtaAction.CouponDetails;
    module = "Coupon";
  } else if (pathname === "/holiday/package-booking") {
    ctaAction = NotificationCtaAction.HolidayBooking;
    module = "Holiday";
    bookingStatus = "InProgress";
  } else if (pathname === "/activity/package-booking") {
    ctaAction = NotificationCtaAction.ActivityBooking;
    module = "Activity";
    bookingStatus = "InProgress";
  } else if (pathname === "/booking-success") {
    module = String(Cookies.get("module") || "Holiday");
    bookingStatus = String(query.bookingStatus ?? query.status ?? "Success");
  } else if (pathname.startsWith("/stays")) {
    module = "Stays";
  } else if (pathname.startsWith("/flights")) {
    module = "Flights";
  } else if (pathname.startsWith("/profile")) {
    module = "Profile";
  } else if (pathname.startsWith("/blog")) {
    module = "Blog";
  } else {
    module = pathname.split("/").filter(Boolean)[0] || "Web";
  }

  if (!module && Cookies.get("module")) {
    module = String(Cookies.get("module"));
  }

  const { searchId, searchText } = pickSearchFields(query);

  return {
    ctaAction,
    module,
    slug,
    searchId,
    searchText,
    cartId: parseCookieNumber("CartId"),
    tempBookingId: parseCookieNumber("id"),
    addToCart: false,
    logId: Number(query.logId ?? query.LogId ?? 0) || 0,
    campaignId: Number(query.campaignId ?? query.CampaignId ?? 0) || 0,
    notificationStatus,
    bookingStatus,
    eventDataJson: buildEventDataJson(router, { ctaAction, module }),
  };
}

function moduleFromCtaAction(ctaAction) {
  switch (Number(ctaAction)) {
    case NotificationCtaAction.HolidayLanding:
    case NotificationCtaAction.HolidayListing:
    case NotificationCtaAction.HolidayDetails:
    case NotificationCtaAction.HolidayBooking:
      return "Holiday";
    case NotificationCtaAction.ActivityLanding:
    case NotificationCtaAction.ActivityListing:
    case NotificationCtaAction.ActivityDetails:
    case NotificationCtaAction.ActivityBooking:
      return "Activity";
    case NotificationCtaAction.Cart:
      return "Cart";
    case NotificationCtaAction.CountryCityPage:
      return "Location";
    case NotificationCtaAction.Deals:
    case NotificationCtaAction.SeasonalDeals:
      return "Deals";
    case NotificationCtaAction.CouponListing:
    case NotificationCtaAction.CouponDetails:
      return "Coupon";
    case NotificationCtaAction.Homepage:
      return "Home";
    default:
      return "";
  }
}

function slugFromCtaPayload(ctaPayload) {
  const data = parseNotificationCtaPayload(ctaPayload);
  return String(
    data.slug ??
      data.Slug ??
      data.holidaySlug ??
      data.activitySlug ??
      data.dealSlug ??
      data.couponSlug ??
      data.placeSlug ??
      ""
  ).trim();
}

export function buildNotificationClickTrackingPayload(item = {}) {
  const ctaPayload = item.ctaPayload;
  const payloadData = parseNotificationCtaPayload(ctaPayload);
  const ctaAction = Number(item.ctaAction ?? payloadData.ctaAction ?? 0) || 0;

  const searchId = Number(
    payloadData.searchId ??
      payloadData.CityId ??
      payloadData.cityId ??
      payloadData.CountryId ??
      payloadData.countryId ??
      0
  );

  const searchText = String(
    payloadData.searchText ??
      payloadData.Keywords ??
      payloadData.keywords ??
      ""
  ).trim();

  return {
    deviceId: getGuestId(),
    userId: Number(getUserId()) || 0,
    countryOrigin: getNotificationCountryOrigin(),
    ctaAction,
    cartId:
      Number(payloadData.cartId ?? payloadData.CartId ?? 0) ||
      parseCookieNumber("CartId"),
    tempBookingId:
      Number(payloadData.tempBookingId ?? payloadData.TempBookingId ?? 0) ||
      parseCookieNumber("id"),
    addToCart: false,
    module: String(
      payloadData.module ?? payloadData.Module ?? moduleFromCtaAction(ctaAction)
    ),
    searchId: Number.isFinite(searchId) ? searchId : 0,
    searchText,
    slug: slugFromCtaPayload(ctaPayload),
    logId: Number(item.logId ?? payloadData.logId ?? payloadData.LogId ?? 0) || 0,
    campaignId:
      Number(
        item.campaignId ??
          payloadData.campaignId ??
          payloadData.CampaignId ??
          0
      ) || 0,
    notificationStatus: NOTIFICATION_STATUS_CLICKED,
    bookingStatus: "",
    eventDataJson: JSON.stringify({
      source: "notification_click",
      notificationId: item.id ?? "",
      title: item.title ?? "",
      ctaAction,
      ctaPayload: payloadData,
    }),
  };
}

export function buildUserTrackingPayload(router, overrides = {}) {
  const routeFields = resolveUserTrackingFromRoute(router);

  return {
    deviceId: getGuestId(),
    userId: Number(getUserId()) || 0,
    countryOrigin: getNotificationCountryOrigin(),
    ctaAction: routeFields.ctaAction,
    cartId: routeFields.cartId,
    tempBookingId: routeFields.tempBookingId,
    addToCart: routeFields.addToCart,
    module: routeFields.module,
    searchId: routeFields.searchId,
    searchText: routeFields.searchText,
    slug: routeFields.slug,
    logId: routeFields.logId,
    campaignId: routeFields.campaignId,
    notificationStatus: routeFields.notificationStatus,
    bookingStatus: routeFields.bookingStatus,
    eventDataJson: routeFields.eventDataJson,
    ...overrides,
  };
}
